import React, { useState, useEffect } from 'react';
import { useParams } from 'react-router-dom';
import { Helmet } from 'react-helmet-async';
import { SocialShareModal } from '@/components';
import {
  Box,
  Typography,
  CircularProgress,
  Alert,
  Paper,
  Chip,
  Stack,
  Button,
} from '@mui/material';
import {
  Download as DownloadIcon,
  Verified as VerifiedIcon,
} from '@mui/icons-material';
import { certificateService, type CertificateResult } from '@/services/certificate/certificateService';
import moment from 'moment';
import jsPDF from 'jspdf';

const CertificateViewPage: React.FC = () => {
  const { attemptId } = useParams<{ attemptId: string }>();
  const [certificateData, setCertificateData] = useState<CertificateResult | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (attemptId) {
      fetchCertificateData();
    }
  }, [attemptId]);


  const fetchCertificateData = async () => {
    if (!attemptId) return;

    try {
      setLoading(true);
      setError(null);
      
      const response = await certificateService.getPublicCertificate(attemptId);

      if (response.success && response.data) {
        setCertificateData(response.data);
      } else {
        setError(response.message || 'Certificate not found or invalid attempt ID');
      }
    } catch (err: any) {
      console.error('Error fetching certificate data:', err);
      
      if (err.message) {
        setError(err.message);
      } else {
        setError('Unable to load certificate. Please try again later.');
      }
    } finally {
      setLoading(false);
    }
  };

  const generateCertificate = async () => {
    if (!certificateData) return;

    const doc = new jsPDF({
      orientation: 'portrait',
      unit: 'mm',
      format: 'a4',
      compress: true // Enable PDF compression
    });

    const pageWidth = 210;
    const pageHeight = 297;

    // Add Montserrat font support
    try {
      // For now, we'll use the Google Fonts Web API approach
      // In production, you would add the actual Montserrat font files
      // doc.addFont('path/to/Montserrat-Regular.ttf', 'Montserrat', 'normal');
      // doc.addFont('path/to/Montserrat-Bold.ttf', 'Montserrat', 'bold');
    } catch (error) {
      console.warn('Montserrat font not available, using helvetica fallback');
    }

    // Function to get the best available font (Montserrat preferred, helvetica fallback)
    const getFont = () => {
      // For now, return helvetica as jsPDF has better support
      // In production, you can check if Montserrat is loaded and return 'Montserrat' or 'helvetica'
      return 'helvetica'; // This will be the closest to Montserrat in jsPDF
    };

    // Function to get difficulty badge image path and number
    const getDifficultyBadgeInfo = (difficulty: string) => {
      switch (difficulty.toLowerCase()) {
        case 'beginner':
          return { imagePath: '/1.png', number: '1' };
        case 'intermediate':
          return { imagePath: '/3.png', number: '2' };
        case 'advanced':
          return { imagePath: '/2.png', number: '3' };
        default:
          return { imagePath: '/1.png', number: '1' };
      }
    };

    // Function to add text and logo overlays on the base image
    const addContentOverlays = () => {
      // Certificate body content - positioned slightly to the right
      const rightOffset = 20; // Move content 20mm to the right
      
      // Y position constants (moved up by 30mm from original positions)
      const Y_POSITIONS = {
        CERTIFY_TEXT: 110,      // was 120
        NAME: 135,             // was 145
        NAME_UNDERLINE: 140,   // was 150
        ACHIEVEMENT: 155,      // was 165
        COURSE_TITLE: 175,     // was 185
        SCORE: 190,            // was 200
        CERT_ID: 210           // was 220
      };
      
      // Date and certificate details at top
      const currentDate = moment().format('DD/MM/YYYY');

      const fontFamily = getFont();
      
      doc.setTextColor(60, 60, 60);
      doc.setFontSize(18);
      doc.setFont(fontFamily, 'normal');
      const certifyText = 'This is to certify that';
      const certifyWidth = doc.getTextWidth(certifyText);
      doc.text(certifyText, (pageWidth - certifyWidth) / 2 + rightOffset, Y_POSITIONS.CERTIFY_TEXT);

      // Recipient name
      doc.setFontSize(32);
      doc.setFont(fontFamily, 'bold');
      doc.setTextColor(40, 40, 40);
      const nameText = certificateData.userName || 'NA';
      const nameWidth = doc.getTextWidth(nameText);
      doc.text(nameText, (pageWidth - nameWidth) / 2 + rightOffset, Y_POSITIONS.NAME);

      // Elegant underline for name
      doc.setDrawColor(70, 130, 180);
      doc.setLineWidth(1);
      doc.line((pageWidth - nameWidth) / 2 + rightOffset - 15, Y_POSITIONS.NAME_UNDERLINE, (pageWidth - nameWidth) / 2 + rightOffset + nameWidth + 15, Y_POSITIONS.NAME_UNDERLINE);

      // Achievement description
      doc.setFontSize(14);
      doc.setFont(fontFamily, 'normal');
      doc.setTextColor(60, 60, 60);
      const achievementText = 'has successfully completed the assessment on, '+currentDate;
      const achievementWidth = doc.getTextWidth(achievementText);
      doc.text(achievementText, (pageWidth - achievementWidth) / 2 + rightOffset, Y_POSITIONS.ACHIEVEMENT);

      // Course title
      doc.setFontSize(18);
      doc.setFont(fontFamily, 'bold');
      doc.setTextColor(70, 130, 180);
      const courseTitle = `"${certificateData.assessmentName}"`;
      const courseTitleWidth = doc.getTextWidth(courseTitle);
      doc.text(courseTitle, (pageWidth - courseTitleWidth) / 2 + rightOffset, Y_POSITIONS.COURSE_TITLE);

      // Score display
      doc.setFontSize(14);
      doc.setFont(fontFamily, 'normal');
      doc.setTextColor(60, 60, 60);
      const scoreText = `with a score of ${certificateData.score}%`;
      const scoreWidth = doc.getTextWidth(scoreText);
      doc.text(scoreText, (pageWidth - scoreWidth) / 2 + rightOffset, Y_POSITIONS.SCORE);

      // Certificate ID below score - centered with right offset
      doc.setFont(fontFamily, 'normal');
      const certIdText = `Certificate ID: ${attemptId?.slice(-8) || 'CERT001'}`;
      const certIdWidth = doc.getTextWidth(certIdText);
      doc.text(certIdText, (pageWidth - certIdWidth) / 2 + rightOffset, Y_POSITIONS.CERT_ID);

      // Add difficulty badge to top right corner
      const difficultyBadgeInfo = getDifficultyBadgeInfo(certificateData.difficulty);
      
      // Load and add the difficulty badge image
      const addDifficultyBadge = () => {
        const badgeImg = new Image();
        badgeImg.onload = () => {
          // Position the badge in the top right corner
          const badgeSize = 25; // 25mm diameter
          const badgeX = pageWidth - badgeSize - 10; // 10mm from right edge
          const badgeY = 10; // 10mm from top edge
          
          // Add the badge image
          doc.addImage(badgeImg, 'PNG', badgeX, badgeY, badgeSize, badgeSize);
          
          // Add the difficulty number in the center of the badge
          doc.setFontSize(16);
          doc.setFont(fontFamily, 'bold');
          doc.setTextColor(255, 255, 255); // White text
          const numberWidth = doc.getTextWidth(difficultyBadgeInfo.number);
          doc.text(
            difficultyBadgeInfo.number, 
            badgeX + (badgeSize - numberWidth) / 2, 
            badgeY + badgeSize / 2 + 3 // Center vertically with slight adjustment
          );
          
          // Save the PDF after adding the badge
          doc.save(`${certificateData.assessmentName.replace(/[^a-z0-9]/gi, '_').toLowerCase()}_certificate.pdf`);
        };
        
        badgeImg.onerror = () => {
          console.warn('Difficulty badge failed to load, saving without badge');
          // Save without badge if image fails to load
          doc.save(`${certificateData.assessmentName.replace(/[^a-z0-9]/gi, '_').toLowerCase()}_certificate.pdf`);
        };
        
        badgeImg.src = difficultyBadgeInfo.imagePath;
      };
      
      // Call function to add badge
      addDifficultyBadge();
    };

    // Load the base certificate template image
    const loadBaseImage = async () => {
      try {
        // Load the certificate template image
        const templateImg = new Image();
        templateImg.onload = () => {
          // Convert image to canvas with compression
          const canvas = document.createElement('canvas');
          const ctx = canvas.getContext('2d');
          
          // Reduce canvas size for compression while maintaining aspect ratio
          const maxWidth = 1240; // Reduce from original size for compression
          const maxHeight = 1754; // A4 portrait ratio
          
          let { width, height } = templateImg;
          
          // Calculate new dimensions maintaining aspect ratio
          if (width > maxWidth || height > maxHeight) {
            const ratio = Math.min(maxWidth / width, maxHeight / height);
            width = width * ratio;
            height = height * ratio;
          }
          
          canvas.width = width;
          canvas.height = height;
          
          // Draw image with better quality settings
          if (ctx) {
            ctx.imageSmoothingEnabled = true;
            ctx.imageSmoothingQuality = 'high';
            ctx.drawImage(templateImg, 0, 0, width, height);
          }
          
          // Convert to JPEG with compression (smaller file size than PNG)
          const templateData = canvas.toDataURL('image/JPEG', 0.85); // 85% quality for compression
          
          // Add the base template image to cover the entire page
          doc.addImage(templateData, 'JPEG', 0, 0, pageWidth, pageHeight);
          
          // Add text content overlays and difficulty badge
          addContentOverlays();
        };
        
        templateImg.onerror = () => {
          console.warn('Base template image failed to load, using fallback');
          // If template fails, continue with text overlays only
          addContentOverlays();
        };
        
        templateImg.src = '/hrtalky certificate.png'; // Load the hrtalky certificate template
      } catch (error) {
        console.warn('Could not load base template:', error);
        addContentOverlays();
      }
    };

    // Start the process by loading the base image
    loadBaseImage();
  };




  if (loading) {
    return (
      <Box
        sx={{
          minHeight: '100vh',
          display: 'flex',
          justifyContent: 'center',
          alignItems: 'center',
        }}
      >
        <CircularProgress size={60} />
      </Box>
    );
  }

  if (error || !certificateData) {
    return (
      <Box
        sx={{
          minHeight: '100vh',
          display: 'flex',
          justifyContent: 'center',
          alignItems: 'center',
          p: 3,
        }}
      >
        <Alert severity="error" sx={{ maxWidth: 500 }}>
          {error || 'Certificate not found'}
        </Alert>
      </Box>
    );
  }


  // Dynamic meta tag data from API
  const certificateTitle = certificateData ? 
    `🏆 ${certificateData.userName} - ${certificateData.assessmentName} Certificate` : 
    'HR Talky Certificate';
    
  const certificateDescription = certificateData ? 
    `Congratulations! ${certificateData.userName} has successfully completed the "${certificateData.assessmentName}" assessment with an outstanding score of ${certificateData.score}% (${certificateData.difficulty} level). Certified on ${moment(certificateData.dateOfAssessment).format('MMMM DD, YYYY')} by HR Talky Assessment Platform.` :
    'Professional HR assessment certificate from HR Talky platform';
    
  const certificateKeywords = certificateData ?
    `${certificateData.userName}, ${certificateData.assessmentName}, certificate, assessment, ${certificateData.difficulty}, ${certificateData.score}%, HR Talky, achievement, professional certification` :
    'certificate, assessment, achievement, hr, talky, professional, certification';
    
  const certificateImage = `${window.location.origin}/hrtalky certificate.png`;
  const currentUrl = window.location.href;
  
  // Additional structured data for better SEO
  const structuredData = certificateData ? {
    "@context": "https://schema.org",
    "@type": "Certificate",
    "name": `${certificateData.assessmentName} Certificate`,
    "description": certificateDescription,
    "recipient": {
      "@type": "Person",
      "name": certificateData.userName
    },
    "issuer": {
      "@type": "Organization",
      "name": "HR Talky",
      "url": window.location.origin
    },
    "dateIssued": certificateData.dateOfAssessment,
    "credentialSubject": certificateData.assessmentName,
    "scoreAchieved": `${certificateData.score}%`,
    "difficultyLevel": certificateData.difficulty
  } : null;

  return (
    <>
      <Helmet>
        <title>{certificateTitle}</title>
        <meta name="description" content={certificateDescription} />
        <meta name="keywords" content={certificateKeywords} />
        
        {/* Open Graph / Facebook */}
        <meta property="og:type" content="article" />
        <meta property="og:url" content={currentUrl} />
        <meta property="og:title" content={certificateTitle} />
        <meta property="og:description" content={certificateDescription} />
        <meta property="og:image" content={certificateImage} />
        <meta property="og:image:width" content="1200" />
        <meta property="og:image:height" content="630" />
        <meta property="og:image:alt" content={`Certificate for ${certificateData?.userName || 'Professional'}`} />
        <meta property="og:site_name" content="HR Talky Assessment Platform" />
        <meta property="og:locale" content="en_US" />
        
        {/* Additional Open Graph data from API */}
        {certificateData && (
          <>
            <meta property="article:author" content={certificateData.userName} />
            <meta property="article:published_time" content={certificateData.dateOfAssessment} />
            <meta property="article:section" content={certificateData.difficulty} />
            <meta property="article:tag" content={certificateData.assessmentName} />
            <meta property="article:tag" content={`Score: ${certificateData.score}%`} />
            <meta property="article:tag" content={certificateData.difficulty} />
          </>
        )}
        
        {/* Twitter Cards */}
        <meta name="twitter:card" content="summary_large_image" />
        <meta name="twitter:site" content="@hrtalky" />
        <meta name="twitter:creator" content="@hrtalky" />
        <meta name="twitter:url" content={currentUrl} />
        <meta name="twitter:title" content={certificateTitle} />
        <meta name="twitter:description" content={certificateDescription} />
        <meta name="twitter:image" content={certificateImage} />
        <meta name="twitter:image:alt" content={`Certificate for ${certificateData?.userName || 'Professional'}`} />
        
        {/* LinkedIn specific */}
        <meta property="linkedin:owner" content="HR Talky" />
        
        {/* Additional SEO meta tags with API data */}
        <meta name="author" content={certificateData?.userName || 'HR Talky'} />
        <meta name="publisher" content="HR Talky Assessment Platform" />
        <meta name="copyright" content="HR Talky Assessment Platform" />
        <meta name="robots" content="index, follow, max-image-preview:large" />
        <meta name="language" content="en" />
        <meta name="rating" content="general" />
        <meta name="distribution" content="global" />
        
        {/* Certificate specific meta tags */}
        {certificateData && (
          <>
            <meta name="certificate:recipient" content={certificateData.userName} />
            <meta name="certificate:assessment" content={certificateData.assessmentName} />
            <meta name="certificate:score" content={`${certificateData.score}%`} />
            <meta name="certificate:difficulty" content={certificateData.difficulty} />
            <meta name="certificate:date" content={moment(certificateData.dateOfAssessment).format('YYYY-MM-DD')} />
            <meta name="certificate:issuer" content="HR Talky Assessment Platform" />
          </>
        )}
        
        <link rel="canonical" href={currentUrl} />
        
        {/* Structured Data JSON-LD */}
        {structuredData && (
          <script type="application/ld+json">
            {JSON.stringify(structuredData)}
          </script>
        )}
      </Helmet>
      
      <Box
        sx={{
          minHeight: '100vh',
          bgcolor: 'background.default',
          p: 3,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
        }}
      >
      <Paper
        elevation={3}
        sx={{
          maxWidth: 800,
          width: '100%',
          borderRadius: 3,
          overflow: 'hidden',
          position: 'relative',
        }}
      >
        {/* Certificate Header */}
        <Box
          sx={{
            bgcolor: 'background.paper',
            p: 4,
            textAlign: 'center',
            borderBottom: '1px solid',
            borderColor: 'divider',
          }}
        >
          <VerifiedIcon sx={{ fontSize: 60, mb: 2, color: 'primary.main' }} />
          <Typography variant="h3" sx={{ fontWeight: 700, mb: 1, color: 'primary.main' }}>
            Certificate of Achievement
          </Typography>
          <Typography variant="h6" sx={{ color: 'text.secondary' }}>
            HR Talky Assessment Platform
          </Typography>
        </Box>

          {/* Certificate Content */}
          <Box sx={{ p: 6, textAlign: 'center', bgcolor: 'background.paper' }}>
            <Typography variant="h5" sx={{ mb: 3, color: 'text.secondary' }}>
              This is to certify that
            </Typography>

            <Typography
              variant="h3"
              sx={{
                fontWeight: 700,
                mb: 3,
                color: 'primary.main',
                textTransform: 'capitalize',
              }}
            >
              {certificateData.userName}
            </Typography>

            <Typography variant="h6" sx={{ mb: 2, color: 'text.secondary' }}>
              has successfully completed the assessment
            </Typography>

            <Typography
              variant="h4"
              sx={{
                fontWeight: 600,
                mb: 4,
                color: 'text.primary',
                fontStyle: 'italic',
              }}
            >
              "{certificateData.assessmentName}"
            </Typography>

            <Stack
              direction="row"
              spacing={2}
              justifyContent="center"
              sx={{ mb: 4 }}
            >
              <Chip
                label={`Score: ${certificateData.score}%`}
                color="success"
                sx={{ fontSize: '1rem', fontWeight: 600 }}
              />
              <Chip
                label={certificateData.difficulty}
                color="primary"
                sx={{ fontSize: '1rem', fontWeight: 600 }}
              />
            </Stack>

            <Typography variant="body1" sx={{ mb: 2, color: 'text.secondary' }}>
              Completed on {moment(certificateData.dateOfAssessment).format('MMMM DD, YYYY')}
            </Typography>

            <Typography variant="body2" sx={{ color: 'text.secondary' }}>
              Certificate ID: {attemptId?.slice(-8)}
            </Typography>
          </Box>

          {/* Action Buttons */}
          <Box
            sx={{
              p: 3,
              bgcolor: 'grey.50',
              borderTop: '1px solid',
              borderColor: 'divider',
              display: 'flex',
              justifyContent: 'center',
              gap: 2,
            }}
          >
            <Button
              variant="contained"
              startIcon={<DownloadIcon />}
              onClick={generateCertificate}
              sx={{
                px: 3,
                py: 1,
                borderRadius: 2,
                textTransform: 'none',
                fontWeight: 600,
              }}
            >
              Download PDF
            </Button>
            <SocialShareModal
              title={certificateTitle}
              description={certificateDescription}
              url={currentUrl}
            />
          </Box>
        </Paper>
      </Box>

    </>
  );
};

export default CertificateViewPage;